import { usePermissionContext } from '@openx/utils/permission/context/PermissionContext.tsx';
import { OPEN_AUDIENCE_ORG_EXTERNAL_ID } from '@openx/utils/services/config.ts';
import { useMemo } from 'react';
import { useDealPromptDomainsContext } from '../utils/context/DealPromptDomainsContext.tsx';
import { mapTaxonomyFilters } from '../utils/mapTaxonomyFilters.ts';
import {
  DEAL_PROMPT_US_REGION_CODE,
  type DealPromptFilters,
  dealPromptFilterSegmentSearchMode,
} from '../utils/types.ts';
import { useDealPromptPermission } from './useDealPromptPermission.ts';

export const useDealPromptAudienceFiltersConfig = (filters?: DealPromptFilters, isAudienceContext = true) => {
  const { session } = usePermissionContext();
  const { isInternalUser } = useDealPromptPermission();

  const isNonUSRegion = Boolean(filters?.region) && filters?.region !== DEAL_PROMPT_US_REGION_CODE;

  const hiddenFilters = useMemo(() => {
    const hidden: string[] = [];

    if (filters?.segment_search_mode === dealPromptFilterSegmentSearchMode.MY_DATA) {
      hidden.push('account');
    }

    if (isNonUSRegion) {
      hidden.push('demographic');
    }

    return hidden;
  }, [filters?.segment_search_mode, isNonUSRegion]);

  const {
    useDealPromptLocationOptions,
    useDealPromptRegionOptions,
    useDealPromptCountryOptions,
    useDealPromptDataPartnerOptions,
    useDealPromptAudienceAccountOptions,
    useDealPromptTaxonomyMetadataOptions,
    useDealPromptAudienceExternalPartnerWithData,
  } = useDealPromptDomainsContext();

  const audienceAccountFilters = useMemo(() => {
    switch (filters?.segment_search_mode) {
      case dealPromptFilterSegmentSearchMode.OXS_PROPRIETARY:
        return {
          accountId: OPEN_AUDIENCE_ORG_EXTERNAL_ID,
        };
      case dealPromptFilterSegmentSearchMode.OXS_DATA_MARKETPLACE:
        return {
          where: {
            has_sellable_segments: {
              _eq: true,
            },
          },
        };
      case dealPromptFilterSegmentSearchMode.MY_DATA:
        return {
          accountId: session?.account.external_id,
        };
      default:
        if (isInternalUser) {
          return {};
        } else {
          return {
            where: {
              has_sellable_segments: {
                _eq: true,
              },
            },
          };
        }
    }
  }, [filters?.segment_search_mode, session?.account.external_id, isInternalUser]);

  const skipGetAccountListOptions = useMemo(() => {
    return (
      isNonUSRegion ||
      filters?.segment_search_mode === dealPromptFilterSegmentSearchMode.MY_DATA ||
      filters?.segment_search_mode === dealPromptFilterSegmentSearchMode.OXS_PROPRIETARY
    );
  }, [isNonUSRegion, filters?.segment_search_mode]);

  const skipAudienceFilterQueries = !isAudienceContext;

  const { data: audienceAccounts = [], loading: audienceAccountsLoading } = useDealPromptAudienceAccountOptions(
    audienceAccountFilters,
    skipGetAccountListOptions || skipAudienceFilterQueries
  );

  const { metadata } = useDealPromptTaxonomyMetadataOptions(skipAudienceFilterQueries);

  const { data: locationOptions } = useDealPromptLocationOptions(skipAudienceFilterQueries);
  const { data: regionOptions = [], loading: regionOptionsLoading } =
    useDealPromptRegionOptions(skipAudienceFilterQueries);
  const { data: countryOptions = [], loading: countryOptionsLoading } = useDealPromptCountryOptions(
    filters?.region,
    skipAudienceFilterQueries
  );

  const { data: dataPartnerOptions = [], loading: dataPartnerOptionsLoading } = useDealPromptDataPartnerOptions(
    filters?.region,
    filters?.country,
    skipAudienceFilterQueries || !isNonUSRegion
  );

  const { data: externalPartnerWithData } = useDealPromptAudienceExternalPartnerWithData(
    {
      accountId: session?.account.external_id,
    },
    skipAudienceFilterQueries
  );

  const { categories, subCategories } = useMemo(() => mapTaxonomyFilters(metadata), [metadata]);

  const accountOptions = useMemo(() => {
    // OXS_PROPRIETARY always shows only OpenXSelect, regardless of region
    if (filters?.segment_search_mode === dealPromptFilterSegmentSearchMode.OXS_PROPRIETARY) {
      return [
        {
          name: 'OpenXSelect',
          id: OPEN_AUDIENCE_ORG_EXTERNAL_ID,
        },
      ];
    }

    if (isNonUSRegion) {
      return dataPartnerOptions;
    }

    const options = audienceAccounts.map(account => ({
      name: account.name,
      id: account.id,
    }));

    if (
      filters?.segment_search_mode === dealPromptFilterSegmentSearchMode.OXS_DATA_MARKETPLACE &&
      !options.some(option => option.id === OPEN_AUDIENCE_ORG_EXTERNAL_ID)
    ) {
      options.push({
        name: 'OpenXSelect',
        id: OPEN_AUDIENCE_ORG_EXTERNAL_ID,
      });
    }

    return options;
  }, [audienceAccounts, dataPartnerOptions, filters?.segment_search_mode, isNonUSRegion]);

  return {
    categories,
    subCategories,
    accountOptions,
    locationOptions,
    regionOptions,
    countryOptions,
    externalPartnerWithData,
    isLoading: audienceAccountsLoading || dataPartnerOptionsLoading,
    regionOptionsLoading,
    countryOptionsLoading,
    hiddenFilters,
  };
};
